"use client";

import { useIsShopsite } from "@context/DomainContext";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { joinClassNames } from "@utils/helpers";
import * as React from "react";

interface StyleProps {
  sharedClasses?: string;
  shopsiteClasses?: string;
  whitelabelClasses?: string;
  className?: string;
}

const Accordion = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root> & StyleProps
>(
  (
    {
      className = "",
      sharedClasses = "",
      shopsiteClasses = "",
      whitelabelClasses = "",
      ...props
    },
    ref
  ) => {
    const isShopsite = useIsShopsite();

    return (
      <AccordionPrimitive.Root
        ref={ref}
        className={joinClassNames(
          className,
          sharedClasses,
          isShopsite ? shopsiteClasses : whitelabelClasses
        )}
        {...props}
      />
    );
  }
);
Accordion.displayName = "Accordion";

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & StyleProps
>(
  (
    {
      className = "",
      sharedClasses = "",
      shopsiteClasses = "",
      whitelabelClasses = "",
      ...props
    },
    ref
  ) => {
    const isShopsite = useIsShopsite();

    return (
      <AccordionPrimitive.Item
        ref={ref}
        className={joinClassNames(
          "border-b",
          className,
          sharedClasses,
          isShopsite ? shopsiteClasses : whitelabelClasses
        )}
        {...props}
      />
    );
  }
);
AccordionItem.displayName = "AccordionItem";

interface TriggerWithCustomIcon
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  icon?: React.ReactNode;
}

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  TriggerWithCustomIcon & StyleProps
>(
  (
    {
      className = "",
      sharedClasses = "",
      shopsiteClasses = "",
      whitelabelClasses = "",
      icon,
      children,
      ...props
    },
    ref
  ) => {
    const isShopsite = useIsShopsite();

    return (
      <AccordionPrimitive.Header className="flex grow w-full">
        <AccordionPrimitive.Trigger
          ref={ref}
          className={joinClassNames(
            "group flex flex-1 items-center justify-between gap-1 py-4 font-medium transition-all [&[data-state=open]>svg]:rotate-180",
            className,
            sharedClasses,
            isShopsite ? shopsiteClasses : whitelabelClasses
          )}
          {...props}
        >
          {children}
          {props.disabled ? (
            <></>
          ) : icon ? (
            icon
          ) : (
            <ChevronDownIcon
              className={joinClassNames(
                "shrink-0 h-5 w-5 transition-transform ease-in-out duration-300 stroke-2",
                isShopsite ? "text-gray-500" : "text-[color:var(--gray-11)]"
              )}
            />
          )}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  }
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

interface ContentStyleProps extends StyleProps {
  animationClassName?: string;
  transitionClassName?: string;
}

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> &
    ContentStyleProps
>(
  (
    {
      className = "",
      sharedClasses = "",
      shopsiteClasses = "",
      whitelabelClasses = "",
      animationClassName = "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      transitionClassName = "transition-all",
      children,
      ...props
    },
    ref
  ) => {
    const isShopsite = useIsShopsite();

    return (
      <AccordionPrimitive.Content
        ref={ref}
        className={joinClassNames(
          "group overflow-hidden text-sm",
          transitionClassName,
          animationClassName,
          className,
          sharedClasses,
          isShopsite ? shopsiteClasses : whitelabelClasses
        )}
        {...props}
      >
        {children}
      </AccordionPrimitive.Content>
    );
  }
);
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
